import { Add01Icon, Logout01Icon, UserIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useShellWidth } from "@/components/layout/shell-width";
import { useAppUpdate } from "@/hooks/use-app-update";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const NAV = [
  {
    to: "/",
    label: "案件",
    match: (path: string) =>
      path === "/" || path.startsWith("/cases"),
  },
  {
    to: "/extract",
    label: "提取",
    match: (path: string) => path.startsWith("/extract"),
  },
  {
    to: "/script-check",
    label: "剧本检测",
    match: (path: string) => path.startsWith("/script-check"),
  },
  {
    to: "/phone-runtime",
    label: "手机运行时",
    match: (path: string) => path.startsWith("/phone-runtime"),
  },
  {
    to: "/users",
    label: "用户",
    match: (path: string) => path.startsWith("/users"),
  },
  {
    to: "/user-groups",
    label: "用户组",
    match: (path: string) => path.startsWith("/user-groups"),
  },
] as const;

export function AppShell({ children }: { children?: ReactNode }) {
  const { updateAvailable, reload } = useAppUpdate();
  const { wide } = useShellWidth();

  return (
    <Toaster>
      <TooltipProvider>
        <div className="flex min-h-svh flex-col bg-background text-foreground">
          {updateAvailable ? (
            <div
              role="status"
              className="sticky top-0 z-50 bg-black text-white"
            >
              <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-6 py-2">
                <p className="text-sm text-white">有新版本可用，请刷新页面以加载更新。</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white bg-transparent text-white hover:bg-white hover:text-black dark:bg-transparent"
                  onClick={reload}
                >
                  刷新
                </Button>
              </div>
            </div>
          ) : null}
          <header className="border-b border-border">
            <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-4 px-6">
              <div className="flex min-w-0 items-center gap-6">
                <Link
                  to="/"
                  className="shrink-0 font-heading text-sm font-medium text-foreground"
                >
                  Peekie Dash
                </Link>
                <ShellNav />
              </div>
              <ShellUserMenu />
            </div>
          </header>
          <main
            className={cn(
              "mx-auto flex w-full flex-1 flex-col gap-4 px-6 py-10",
              wide ? "max-w-none" : "max-w-6xl",
            )}
          >
            {children}
          </main>
        </div>
      </TooltipProvider>
    </Toaster>
  );
}

function ShellNav() {
  const auth = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (auth.status !== "authed") return null;
  // Hide top nav on login; show on app pages (including case detail).
  if (pathname.startsWith("/login")) return null;

  return (
    <nav className="flex items-center gap-1">
      {NAV.map((item) => {
        const active = item.match(pathname);
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              "rounded-md px-2.5 py-1.5 text-sm font-medium text-foreground/60 transition-colors hover:text-foreground",
              active && "bg-muted text-foreground",
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function ShellUserMenu() {
  const auth = useAuth();
  const navigate = useNavigate();

  if (auth.status !== "authed") return null;

  const onSignOut = async () => {
    await auth.logout();
    void navigate({ to: "/login", search: { returnTo: "/", error: undefined } });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button variant="outline" className="max-w-56" />}
      >
        <HugeiconsIcon icon={UserIcon} data-icon="inline-start" />
        <span className="truncate">{auth.me.email}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="truncate">
            {auth.me.email}
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => {
              void navigate({ to: "/cases/new" });
            }}
          >
            <HugeiconsIcon icon={Add01Icon} />
            新建案件
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem variant="destructive" onClick={() => void onSignOut()}>
            <HugeiconsIcon icon={Logout01Icon} />
            退出登录
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
